import voca from "voca";
import moment from "moment";

import {
  BusinessAttributes,
  BusinessTypeEnum,
} from "../attributes/business-attributes";
import { parseDate } from "../common/Hooks";
import BaseModel from "./BaseModel";
import User from "./User";
import BusinessCategory from "./BusinessCategory";
import Currency from "./Currency";
import { DD_MM_YYYY_FORMAT, YYYY_MM_DD_FORMAT } from "../settings/constants";
import { MemberStatus } from "../settings/MemberStatus";
import { DocumentAttributes } from "../attributes/document-attributes";
import Document from "./Document";
import { BusinessAccountAttributes } from "../attributes/business-account-attributes";
import BusinessAccount from "./BusinessAccount";
import { WalletType } from "../settings/WalletType";
import BankAccount from "./BankAccount";

export enum BusinessScopes {
  docs = "docs",
  accounts = "accounts",
  owner = "owner",
  bank = "bank",
}

class Business extends BaseModel {
  id: number;
  company_logo: string;
  company_name: string;
  company_address: string;
  business_type: number;
  activated_at: Date | null;
  is_retailer: number;
  company_email: string;
  primary_phone_number: string;
  secondary_phone_number: string;
  message: string;
  start_date: Date | null;
  approved_at: Date | null;
  declined_at: Date | null;
  requested_more_at: Date | null;

  universal_discount: number;
  toll_discount: number;
  fuel_discount: number;
  communication_discount: number;
  withdraw_fees: number;
  withdraw_commission_percent: number;

  owner?: User;
  business_category?: BusinessCategory;
  business_currency?: Currency;
  documents?: Array<Document>;
  accounts?: Array<BusinessAccount>;
  bank_account?: BankAccount;

  constructor(
    id: number,
    company_logo: string,
    company_name: string,
    company_address: string,
    business_type: number,
    activated_at: Date | null,
    created_at: Date | null,
    is_retailer: number,
    company_email: string,
    primary_phone_number: string,
    secondary_phone_number: string,
    start_date: Date | null,
    approved_at: Date | null,
    declined_at: Date | null,
    requested_more_at: Date | null,
    universal_discount: number,
    toll_discount: number,
    fuel_discount: number,
    communication_discount: number,
    withdraw_fees: number,
    withdraw_commission_percent: number,
    message: string,
    owner: User,
    business_category: BusinessCategory,
    business_currency: Currency,
    documents: Array<Document>,
    accounts: Array<BusinessAccount>,
    bank_account: BankAccount
  ) {
    super(created_at);

    this.id = id;
    this.company_logo = company_logo;
    this.company_name = company_name;
    this.company_address = company_address;
    this.business_type = business_type;
    this.activated_at = activated_at;
    this.is_retailer = is_retailer;
    this.company_email = company_email;
    this.primary_phone_number = primary_phone_number;
    this.secondary_phone_number = secondary_phone_number;
    this.start_date = start_date;
    this.approved_at = approved_at;
    this.declined_at = declined_at;
    this.requested_more_at = requested_more_at;
    this.message = message;

    this.universal_discount = universal_discount;
    this.toll_discount = toll_discount;
    this.fuel_discount = fuel_discount;
    this.communication_discount = communication_discount;
    this.withdraw_fees = withdraw_fees;
    this.withdraw_commission_percent = withdraw_commission_percent;

    this.owner = owner;
    this.business_category = business_category;
    this.business_currency = business_currency;
    this.documents = documents;
    this.accounts = accounts;
    this.bank_account = bank_account;
  }

  static fromJson(json: BusinessAttributes): Business | null {
    if (json === null || json === undefined) {
      return null;
    }

    return new Business(
      json.id,
      json.company_logo,
      json.company_name,
      json.company_address,
      json.business_type,
      parseDate(json.activated_at),
      parseDate(json.created_at),
      json.is_retailer,
      json.company_email,
      json.primary_phone_number,
      json.secondary_phone_number,
      parseDate(json.start_date),
      parseDate(json.approved_at),
      parseDate(json.declined_at),
      parseDate(json.requested_more_at),

      json.universal_discount,
      json.toll_discount,
      json.fuel_discount,
      json.communication_discount,
      json.withdraw_fees,
      json.withdraw_commission_percent,
      json.message,

      User.fromJson(json.owner),
      BusinessCategory.fromJson(json.business_category),
      Currency.fromJson(json.business_currency),
      Business.jsonToDocs(json.documents),
      Business.jsonToAccounts(json.accounts),
      BankAccount.fromJson(json.bank_account)
    );
  }

  static jsonToAccounts(
    json: Array<BusinessAccountAttributes>
  ): Array<BusinessAccount> {
    if (json === null || json === undefined) {
      return [];
    }

    return json.map((item) => BusinessAccount.fromJson(item));
  }

  static jsonToDocs(json: Array<DocumentAttributes>): Array<Document> {
    if (json === null || json === undefined) {
      return [];
    }

    return json.map((item) => Document.fromJson(item));
  }

  fullName = (): string => {
    return voca.titleCase(this.company_name);
  };

  address = (): string => {
    return voca.titleCase(this.company_address);
  };

  avatar = (): string => {
    if (this.company_logo !== null) {
      return this.company_logo;
    }

    return "";
  };

  category = (): string => {
    if (this.business_category !== null) {
      return voca.titleCase(this.business_category.designation);
    }

    return null;
  };

  categoryToString = (): string => {
    if (this.business_category !== null) {
      return this.business_category.designation;
    }

    return "";
  };

  businessTypeToString = (): string => {
    if (this.business_type === BusinessTypeEnum.personal) {
      return "sharedScreen.personal";
    }

    return "sharedScreen.enterprise";
  };

  isRetailerToString = (): string => {
    if (this.is_retailer === 1) {
      return "sharedScreen.yes";
    }

    return "sharedScreen.no";
  };

  currency = (): string => {
    if (this.business_currency !== null) {
      return `${voca.titleCase(
        this.business_currency.designation
      )} (${this.business_currency.code.toUpperCase()})`;
    }

    return "";
  };

  startDate = (): string => {
    if (this.start_date !== null) {
      return moment(this.start_date, YYYY_MM_DD_FORMAT).format(
        DD_MM_YYYY_FORMAT
      );
    }

    return "";
  };

  getMessage = (): string => {
    return this.message;
  };

  status = (): MemberStatus => {
    if (this.requested_more_at !== null) {
      return MemberStatus.pending;
    }

    if (this.approved_at !== null) {
      return MemberStatus.approved;
    }

    if (this.declined_at !== null) {
      return MemberStatus.declined;
    }

    return MemberStatus.new;
  };

  isApproved(): boolean {
    return this.approved_at !== null;
  }

  isDeclined(): boolean {
    return this.declined_at !== null;
  }

  isPending(): boolean {
    return this.requested_more_at !== null;
  }

  getDiscount(account: BusinessAccount): number {
    switch (account.wallet_type) {
      case WalletType.universal:
        return this.universal_discount;
      case WalletType.fuel:
        return this.fuel_discount;
      case WalletType.toll:
        return this.toll_discount;
      case WalletType.communication:
        return this.communication_discount;
    }

    return 0.0;
  }

  currencyCode(): string {
    return this.business_currency.code.toString().toUpperCase();
  }

  // Set

  setAccounts(accounts: Array<BusinessAccount>): void {
    this.accounts = accounts;
  }
}

export default Business;
